import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Autorenew,
  CalendarToday,
  Close,
  PersonPinOutlined,
  PlaylistAddCheck,
  Search,
  Update,
  VerifiedUserOutlined,
  VisibilityOff,
  WifiTethering,
} from '@mui/icons-material';
import { Box, Button, Input, InputAdornment, Menu, MenuItem, PopoverPosition } from '@mui/material';
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { sort } from 'fast-sort';
import {
  Estate,
  EstateTaskGroup,
  EstateTaskInstance,
  EstateTaskInstanceStatus,
  FeatureSubscriptionType,
  PortalPermissions,
  useEstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery,
  useEstateTaskInstance_UpdateAssignedUserMutation,
  useEstateTaskInstance_UserDuplicateEstateTaskInstanceMutation,
  useEstateTaskInstance_UserUpdateIsHiddenMutation,
  useEstateTaskInstance_UserUpdateStatusToCompletedMutation,
  useEstateTaskInstance_UserUpdateStatusToUnassignedMutation,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';

import Spinner from 'common/spinner';
import AssignedTaskDrawer from 'pages/assigned-tasks-page/components/assigned-task-drawer';
import CreatePaymentModal from 'pages/common/estate/create-payment-modal';
import { UserAutoCompleteOption } from 'pages/common/portal/portal-users-autocomplete';
import { canAddPointPerson, canAssignTasks } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import EstateTaskInstancePaymentForm from './estate-task/estate-task-instance-payment-form';
import { LabelCell } from './estate-task/estate-task-label-cell';
import { PointPersonCell } from './estate-task/estate-task-point-person-cell';
import { PushToTrustateCell } from './estate-task/estate-task-push-to-trustate-cell';
import { QuickActionsCell } from './estate-task/estate-task-quick-actions-cell';
import { StatusCellMemoized } from './estate-task/estate-task-status-cell';
import { useExpandedGroups } from './estate-task-custom/estate-task-custom-hooks';
import EstateTaskCustomModal from './estate-task-custom/estate-task-custom-modal';
import EstateTaskInstanceCustomDeadlinePopover from './estate-task-custom/estate-task-instance-custom-deadline-popover';
import { EstateChecklistStartNewSearchMenu } from './estate-checklist-start-new-search-menu';
import HideEstateTaskModal from './hide-estate-task-modal';
import PlanPaywallModal from './plan-paywall-modal';
import { TaskGroupAccordion } from './task-group-accordion';
import { useStyles } from './task-groups.styles';

interface TaskGroupProps {
  estate: Estate;
  // eslint-disable-next-line @typescript-eslint/ban-types
  estateSearchStatusButton: Function;
  showHiddenTasks: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setShowHiddenTasks: Function;
  onEditClientMatterClick: () => void;
  featureSubscriptionType: FeatureSubscriptionType;
  refetchEstateTaskInstanceCompletionPercentage?: Function;
}

type AssignedUsersUpdating = {
  [key: string]: boolean;
};

type GroupedEstateTaskInstances = Record<string, EstateTaskInstance[]>;

const sortInstances = (instances: EstateTaskInstance[]) =>
  sort(instances).asc([
    (instance) => instance.sortOrder,
    (instance) => new Date(instance.createdAt).getTime(),
  ]);

const useGroupedEstateTaskInstances = (initial: GroupedEstateTaskInstances = {}) => {
  const [groupedEstateTaskInstances, setGroupedEstateTaskInstances] = useState(initial);

  const setAll = (group: string, instances: EstateTaskInstance[]) => {
    setGroupedEstateTaskInstances((prevState) => ({
      ...prevState,
      [group]: instances,
    }));
  };

  const add = (group: string, instance: EstateTaskInstance) => {
    setGroupedEstateTaskInstances((prevState) => ({
      ...prevState,
      [group]: sortInstances([...(prevState[group] ?? []), instance]),
    }));
  };

  const remove = (group: string, instanceId: string) => {
    setGroupedEstateTaskInstances((prevState) => ({
      ...prevState,
      [group]: (prevState[group] ?? []).filter((instance) => instance.id !== instanceId),
    }));
  };

  const replace = (group: string, oldId: string, newInstance: EstateTaskInstance) => {
    setGroupedEstateTaskInstances((prevState) => ({
      ...prevState,
      [group]: (prevState[group] ?? []).map((instance) =>
        instance.id === oldId ? newInstance : instance,
      ),
    }));
  };

  return {
    groupedEstateTaskInstances,
    setFilteredEstateTaskInstances: setGroupedEstateTaskInstances,
    setGroupedEstateTaskInstances: setAll,
    addGroupedEstateTaskInstance: add,
    removeGroupedEstateTaskInstance: remove,
    replaceGroupedEstateTaskInstance: replace,
  };
};

const initialGroupedEstateTaskInstances: GroupedEstateTaskInstances = {};

const initialAssignedUsersUpdating: AssignedUsersUpdating = {};

export const TaskGroups = ({
  estate,
  estateSearchStatusButton,
  showHiddenTasks,
  setShowHiddenTasks,
  featureSubscriptionType,
  refetchEstateTaskInstanceCompletionPercentage,
}: TaskGroupProps) => {
  const classes = useStyles();

  const { portalId, estateId } = useParams<Record<string, string>>();

  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);

  const { expandedGroups, setExpandedGroup } = useExpandedGroups();
  const {
    groupedEstateTaskInstances,
    setFilteredEstateTaskInstances,
    setGroupedEstateTaskInstances,
    addGroupedEstateTaskInstance,
    replaceGroupedEstateTaskInstance,
  } = useGroupedEstateTaskInstances(initialGroupedEstateTaskInstances);

  const originalGroupedEstateTaskInstances = useRef<GroupedEstateTaskInstances>();

  const checkboxRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [addCustomDeadlineButtonAnchorPosition, setAddCustomDeadlineButtonAnchorPosition] =
    useState<PopoverPosition | null>(null);
  const [addCustomTaskGroup, setAddCustomTaskGroup] = useState<EstateTaskGroup>();
  const [assignedUsersUpdating, setAssignedUsersUpdating] = useState<AssignedUsersUpdating>(
    initialAssignedUsersUpdating,
  );
  const [completedTasksCount, setCompletedTasksCount] = useState<number>(0);
  const [currentEstateTaskInstance, setCurrentEstateTaskInstance] =
    useState<EstateTaskInstance | null>(null);
  const [estateTaskInstanceCurrentlyEditing, setEstateTaskInstanceCurrentlyEditing] = useState<
    EstateTaskInstance | undefined
  >(undefined);
  const [estateTaskInstanceIdUpdatingPointPerson, setEstateTaskInstanceIdUpdatingPointPerson] =
    useState('');
  const [estateTaskInstanceMenuAnchorEl, setEstateTaskInstanceMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [minimumFeatureSubscriptionType, setMinimumFeatureSubscriptionType] =
    useState<FeatureSubscriptionType | null>(null);
  const [searchTask, setSearchTask] = useState<string>('');
  const [showPlanPaywallModal, setShowPlanPaywallModal] = useState(false);
  const [showEstateTaskCustomModal, setShowEstateTaskCustomModal] = useState(false);
  const [showDelegatePaymentModal, setShowDelegatePaymentModal] = useState(false);
  const [showTaskInstanceMenu, setShowTaskInstanceMenu] = useState<boolean>(false);
  const [showAssignedTaskDrawer, setShowAssignedTaskDrawer] = useState<boolean>(false);
  const [showHideEstateTaskModal, setShowHideEstateTaskModal] = useState<boolean>(false);
  const [showHiddenTasksMap, setShowHiddenTasksMap] = useState<Record<string, boolean>>({});
  const [
    showEstateTaskInstanceCustomDeadlinePopover,
    setShowEstateTaskInstanceCustomDeadlinePopover,
  ] = useState<boolean>(false);

  const storageKey = 'MatterDashboardDisplayCollapse-' + estate.type + '-' + estate.portalId;

  const groupedEstateTaskInstancesAsArray = useMemo(
    () => Object.values(groupedEstateTaskInstances).flat(),
    [groupedEstateTaskInstances],
  );

  const estateSearchTaskInstances = useMemo(
    () => groupedEstateTaskInstancesAsArray.filter((instances) => instances.isSearch),
    [groupedEstateTaskInstancesAsArray],
  );

  const setUserUpdating = (estateTaskInstanceId: string, isUpdating: boolean) => {
    setAssignedUsersUpdating((prev) => ({
      ...prev,
      [estateTaskInstanceId]: isUpdating,
    }));
  };

  const [updateAssignedUser] = useEstateTaskInstance_UpdateAssignedUserMutation({
    onCompleted: (updatedEstateTaskInstanceData) => {
      const updatedEstateTaskInstance = updatedEstateTaskInstanceData?.data;
      if (updatedEstateTaskInstance) {
        const clientEstateTaskInstance =
          estateSearchTaskInstances.find(
            (instance) => instance.id === updatedEstateTaskInstance.id,
          ) ||
          groupedEstateTaskInstancesAsArray.find(
            (instance) => instance.id === updatedEstateTaskInstance.id,
          );
        replaceGroupedEstateTaskInstance(
          clientEstateTaskInstance!.group as string,
          clientEstateTaskInstance!.id,
          updatedEstateTaskInstance as EstateTaskInstance,
        );
        setUserUpdating(updatedEstateTaskInstance.id, false);
      }
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating estate task instance: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [markTaskCompleted] = useEstateTaskInstance_UserUpdateStatusToCompletedMutation({
    onError: (e) => {
      showAlert({
        message: 'Error user updating status to completed: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [markTaskUnassigned] = useEstateTaskInstance_UserUpdateStatusToUnassignedMutation({
    onError: (e) => {
      showAlert({
        message: 'Error user updating status to unassigned: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [duplicateTask] = useEstateTaskInstance_UserDuplicateEstateTaskInstanceMutation({
    onCompleted: ({ data: newEstateTaskInstance }) => {
      if (newEstateTaskInstance) {
        addGroupedEstateTaskInstance(
          newEstateTaskInstance.group as string,
          newEstateTaskInstance as EstateTaskInstance,
        );
        showToast({
          message: 'Duplicate task ready to assign',
          severity: NotificationSeverity.SUCCESS,
        });
      }
    },
    onError: (e) => {
      showAlert({
        message: 'Error duplicating task: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [markTaskHidden] = useEstateTaskInstance_UserUpdateIsHiddenMutation({
    onCompleted: () => {
      setShowHideEstateTaskModal(false);
      showToast({
        message: showHiddenTasks
          ? 'Task successfully moved out of hidden tasks.'
          : 'Task successfully moved to hidden tasks.',
        severity: NotificationSeverity.SUCCESS,
      });
    },
    onError: (e) => {
      showAlert({
        message: 'Error user updating task visibility: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
    });

  const { data: taskData, loading: taskLoading } =
    useEstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: { input: { estateId, portalId } },
      onError: (e) => {
        showAlert({
          message: 'Error getting all estate task instances by estate ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (!Object.keys(groupedEstateTaskInstances).length && taskData) {
    // pre-process the task data
    const taskGroups = [
      ...new Set(taskData.data.map((estateTaskInstance) => estateTaskInstance.group!)),
    ];

    for (const group of taskGroups) {
      // get the task instances for each task group and filter out archived instances
      //  filtering archived instances on client instead of server because of the complicated
      //  logic around displaying the instances. doing it this way would make it easier to support
      //  an archived tasks view (like how it supports a hidden tasks view)
      // if task was assigned it should be visible even if it's archived
      const newTaskGroup = taskData.data.filter(
        (estateTaskInstance) =>
          estateTaskInstance.group === group &&
          (!estateTaskInstance.isArchived || !!estateTaskInstance.estateWorkItemId),
      );
      setGroupedEstateTaskInstances(group, newTaskGroup as EstateTaskInstance[]);
      if (!localStorage.getItem(storageKey) || localStorage.getItem(storageKey) === '{}') {
        setExpandedGroup(group, true, storageKey);
      }
    }
  }

  const showPortalUsersAutocompleteElement = (estateTaskInstanceId: string) => {
    if (!canAddPointPerson(featureSubscriptionType)) {
      showToast({
        message: 'Please upgrade your account to add a point person to this task.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    setEstateTaskInstanceIdUpdatingPointPerson(estateTaskInstanceId);
  };

  const hidePortalUsersAutocompleteElement = (_estateTaskInstanceId: string) => {
    setEstateTaskInstanceIdUpdatingPointPerson('');
  };

  const handleAssignedUserChange = (
    elementId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _e: any,
    option: UserAutoCompleteOption | null,
  ) => {
    const estateTaskInstanceId = elementId.replace('assignedUserId_', '');
    const assignedUserId = option?.id || null;

    setUserUpdating(estateTaskInstanceId, true);

    updateAssignedUser({
      variables: {
        input: { id: estateTaskInstanceId, assignedUserId },
      },
    });

    hidePortalUsersAutocompleteElement(estateTaskInstanceId);
  };

  const handleEstateTaskInstanceMenuClose = () => {
    setEstateTaskInstanceMenuAnchorEl(null);
    setShowTaskInstanceMenu(false);
  };

  const handleAddDeadlineClick = (event: any) => {
    // NOTE: The reason why we aren't just anchoring the popover straight to the
    //  anchor element is because the anchor element in this case is the menu which
    //  is memoized. If using memoizaiton you need to use absolute coordinates.
    const anchorEl = event.currentTarget;
    const rect = anchorEl.getBoundingClientRect();
    setAddCustomDeadlineButtonAnchorPosition({
      top: rect.top,
      left: rect.left - rect.width - 30,
    });
    setShowEstateTaskInstanceCustomDeadlinePopover(true);
  };

  const handleTaskActivityClick = () => {
    setShowAssignedTaskDrawer(true);
  };

  const handleAssignAgainClick = (estateTaskInstance: EstateTaskInstance) => {
    duplicateTask({
      variables: { input: { id: estateTaskInstance.id, portalId } },
    });
  };

  const handleHideTaskClick = () => {
    setShowHideEstateTaskModal(true);
  };

  const handleAssignClick = useCallback(
    (estateTaskInstance: EstateTaskInstance) => {
      if (!canAssignTasks(featureSubscriptionType)) {
        showToast({
          message: 'Please upgrade your account to push this task to Trustate.',
          severity: NotificationSeverity.WARNING,
        });
        return;
      }

      setEstateTaskInstanceCurrentlyEditing(estateTaskInstance);
      setShowDelegatePaymentModal(true);
    },
    [featureSubscriptionType, setShowDelegatePaymentModal],
  );

  // const handleEstateTaskInstanceMenuClick = (event: React.MouseEvent<HTMLElement>) => {
  //   setEstateTaskInstanceMenuAnchorEl(event.currentTarget);
  //   setShowTaskInstanceMenu(true);
  // };

  const handleHideTaskCurrentDashboardClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    estateTaskInstance: EstateTaskInstance,
    isHidden: boolean,
  ) => {
    replaceGroupedEstateTaskInstance(estateTaskInstance.group as string, estateTaskInstance.id, {
      ...estateTaskInstance,
      isHidden,
    });
    if (estateTaskInstance.status === EstateTaskInstanceStatus.Completed) {
      setCompletedTasksCount(completedTasksCount - 1);
    }
    markTaskHidden({
      variables: {
        input: { id: estateTaskInstance.id, portalId, isHidden, updateAllEstates: false },
      },
    });
  };

  const handleHideTaskAllDashboardsClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    estateTaskInstance: EstateTaskInstance,
    portalId: string,
    isHidden: boolean,
  ) => {
    replaceGroupedEstateTaskInstance(estateTaskInstance.group as string, estateTaskInstance.id, {
      ...estateTaskInstance,
      isHidden,
    });
    if (estateTaskInstance.status === EstateTaskInstanceStatus.Completed) {
      setCompletedTasksCount(completedTasksCount - 1);
    }
    markTaskHidden({
      variables: {
        input: { id: estateTaskInstance.id, portalId, isHidden, updateAllEstates: true },
      },
    });
  };

  const handleToggleTaskStatus = useCallback(
    async (estateTaskInstance: EstateTaskInstance) => {
      const isTaskImmutable =
        estateTaskInstance.status === EstateTaskInstanceStatus.Assigned ||
        (estateTaskInstance.status === EstateTaskInstanceStatus.Completed &&
          estateTaskInstance.estateWorkItemId);
      if (isTaskImmutable) {
        if (refetchEstateTaskInstanceCompletionPercentage) {
          await refetchEstateTaskInstanceCompletionPercentage({
            input: {
              estateId,
              portalId,
            },
          });
        }
        return;
      }

      const clientEstateTaskInstance =
        estateSearchTaskInstances.find((instance) => instance.id === estateTaskInstance.id) ||
        groupedEstateTaskInstancesAsArray.find((instance) => instance.id === estateTaskInstance.id);

      if (!clientEstateTaskInstance) {
        showAlert({
          message: 'Estate Task Instance not found.',
          severity: NotificationSeverity.ERROR,
        });
        return;
      }
      const isCompleted = clientEstateTaskInstance.status === EstateTaskInstanceStatus.Completed;

      const newStatus = isCompleted
        ? EstateTaskInstanceStatus.Unassigned
        : EstateTaskInstanceStatus.Completed;

      await replaceGroupedEstateTaskInstance(
        clientEstateTaskInstance.group as string,
        clientEstateTaskInstance.id,
        { ...clientEstateTaskInstance, status: newStatus },
      );

      if (isCompleted) {
        await markTaskUnassigned({
          variables: {
            id: clientEstateTaskInstance.id,
          },
        });
      } else {
        await markTaskCompleted({
          variables: {
            id: clientEstateTaskInstance.id,
          },
        });
      }
      if (refetchEstateTaskInstanceCompletionPercentage) {
        await refetchEstateTaskInstanceCompletionPercentage({
          input: {
            estateId,
            portalId,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      completedTasksCount,
      markTaskCompleted,
      markTaskUnassigned,
      estateSearchTaskInstances,
      replaceGroupedEstateTaskInstance,
      groupedEstateTaskInstances,
      groupedEstateTaskInstancesAsArray,
    ],
  );

  const handleEstateTaskCustomClick = (group: EstateTaskGroup) => {
    setAddCustomTaskGroup(group);
    setShowEstateTaskCustomModal(true);
  };

  const handleShowHiddenTasks = (group: string) => {
    setShowHiddenTasksMap((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const handleCancelSearch = () => {
    if (isSearching) {
      setIsSearching(false);
      setSearchTask('');
    }

    setFilteredEstateTaskInstances(originalGroupedEstateTaskInstances.current!);
    return;
  };

  const handleSearch = () => {
    if (!isSearching && searchTask) {
      setIsSearching(true);
    }

    if (!searchTask) {
      setFilteredEstateTaskInstances(originalGroupedEstateTaskInstances.current!);
      return;
    }

    const lowerSearch = searchTask.toLowerCase();

    const filtered = Object.entries(originalGroupedEstateTaskInstances.current!).reduce(
      (acc, [group, estateTaskInstance]) => {
        const matches = estateTaskInstance.filter(
          (estateTaskInstance) =>
            estateTaskInstance.label?.toLowerCase().includes(lowerSearch) ||
            estateTaskInstance.labelHint?.toLowerCase().includes(lowerSearch),
        );

        if (matches.length > 0) {
          acc[group] = matches;
        }

        return acc;
      },
      {} as GroupedEstateTaskInstances,
    );

    setFilteredEstateTaskInstances(filtered);
  };

  useEffect(() => {
    if (
      !originalGroupedEstateTaskInstances.current &&
      Object.keys(groupedEstateTaskInstances).length > 0
    ) {
      originalGroupedEstateTaskInstances.current = groupedEstateTaskInstances;
    }
  }, [groupedEstateTaskInstances]);

  // Commented the Checkbox cell to keep it for future use
  // const CheckboxCellMemoized = memo(({ row }: GridRenderCellParams) => {
  //   const estateTaskInstance = row as EstateTaskInstance;
  //   const clientEstateTaskInstance =
  //     estateTaskInstances.find(
  //       (estateTaskInstanceSearch) => estateTaskInstanceSearch.id === estateTaskInstance.id,
  //     )! || estateSearchTaskInstances.find((instance) => instance.id === estateTaskInstance.id);
  //   return (
  //     <Checkbox
  //       className={classes.checkbox}
  //       color="default"
  //       inputRef={(estateTaskInstanceId) =>
  //         (checkboxRefs.current[clientEstateTaskInstance.id] = estateTaskInstanceId)
  //       }
  //       checked={clientEstateTaskInstance.status === EstateTaskInstanceStatus.Completed}
  //       disableRipple
  //       disableFocusRipple
  //       disabled={
  //         clientEstateTaskInstance.status === EstateTaskInstanceStatus.Assigned ||
  //         (clientEstateTaskInstance.status === EstateTaskInstanceStatus.Completed &&
  //           !!clientEstateTaskInstance.estateWorkItemId)
  //       }
  //       onClick={() => {
  //         handleToggleCheckbox(clientEstateTaskInstance);
  //       }}
  //     />
  //   );
  // });

  const EstateTaskInstanceMenuMemoized = memo((estateTaskInstance: EstateTaskInstance) => {
    const disableAssignAgain =
      !estateTaskInstance.estateWorkItemId ||
      !estateTaskInstance.userCanAssignAgain ||
      featureSubscriptionTypeData!.data === FeatureSubscriptionType.Trial;
    return (
      <Menu
        id={`estate-task-instance-menu-${estateTaskInstance.id}`}
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={estateTaskInstanceMenuAnchorEl}
        open={showTaskInstanceMenu}
        onClose={handleEstateTaskInstanceMenuClose}
        PaperProps={{
          style: {
            width: '20ch',
          },
        }}
        TransitionProps={{
          timeout: 0,
        }}
      >
        <MenuItem key="add deadline">
          <Button
            className={classes.hideTaskButton}
            size="small"
            startIcon={<CalendarToday />}
            onClick={handleAddDeadlineClick}
          >
            Add/Edit Deadline
          </Button>
        </MenuItem>
        <MenuItem
          key="task activity"
          onClick={handleEstateTaskInstanceMenuClose}
          disabled={!estateTaskInstance.estateWorkItemId}
        >
          <Button
            className={classes.hideTaskButton}
            size="small"
            startIcon={<Update />}
            onClick={handleTaskActivityClick}
          >
            Task Activity
          </Button>
        </MenuItem>
        <MenuItem
          key="assign again"
          onClick={handleEstateTaskInstanceMenuClose}
          disabled={disableAssignAgain}
        >
          <Button
            className={classes.hideTaskButton}
            size="small"
            startIcon={<Autorenew />}
            onClick={() => handleAssignAgainClick(estateTaskInstance)}
          >
            Assign Again
          </Button>
        </MenuItem>
        <MenuItem key="hide task" onClick={handleEstateTaskInstanceMenuClose}>
          <Button
            className={classes.hideTaskButton}
            size="small"
            startIcon={<VisibilityOff />}
            onClick={handleHideTaskClick}
          >
            {showHiddenTasks ? 'Unhide task' : 'Hide task'}
          </Button>
        </MenuItem>
      </Menu>
    );
  });

  const estateTaskColumns: GridColDef[] = [];

  // Commenting out the checkboxes section incase it is needed for future updates.
  // estateTaskColumns.push({
  //   field: 'checkbox',
  //   headerName: '',
  //   editable: false,
  //   width: 25,
  //   sortable: false,
  //   hideSortIcons: true,
  //   headerClassName: 'checkbox-column',
  //   align: 'center',
  //   renderCell: (params: GridRenderCellParams) => <CheckboxCellMemoized {...params} />,
  // });

  estateTaskColumns.push({
    field: 'label',
    headerName: 'Task',
    flex: 6,
    headerClassName: 'estateTasksHeader',
    renderHeader: (params: GridColumnHeaderParams) => (
      <Box className={classes.estateTaskHeader}>
        <PlaylistAddCheck />
        <span className={classes.estateTaskHeaderName}>{params.colDef.headerName}</span>
      </Box>
    ),
    renderCell: (params: GridRenderCellParams) => (
      <LabelCell
        {...params}
        estateSearchTaskInstances={estateSearchTaskInstances}
        groupedEstateTaskInstancesAsArray={groupedEstateTaskInstancesAsArray}
        featureSubscriptionType={featureSubscriptionType}
        classes={classes}
        handleAddDeadlineClick={handleAddDeadlineClick}
        setCurrentEstateTaskInstance={setCurrentEstateTaskInstance}
      />
    ),
  });

  estateTaskColumns.push({
    field: 'status',
    headerName: 'Status',
    flex: 2,
    headerClassName: 'estateTasksHeader',
    renderHeader: (params: GridColumnHeaderParams) => (
      <Box className={classes.estateTaskHeader}>
        <WifiTethering />
        <span className={classes.estateTaskHeaderName}>{params.colDef.headerName}</span>
      </Box>
    ),
    renderCell: (params: GridRenderCellParams) => (
      <StatusCellMemoized
        {...params}
        estate={estate}
        setCurrentEstateTaskInstance={setCurrentEstateTaskInstance}
        setShowAssignedTaskDrawer={setShowAssignedTaskDrawer}
      />
    ),
  });

  if (hasPortalPermissionAdmin || hasPortalPermissionUser) {
    estateTaskColumns.push({
      field: 'pushToTrustate',
      headerName: 'Start Task',
      flex: 2.5,
      align: 'center',
      headerClassName: 'estateTasksHeader',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box className={classes.estateTaskHeader}>
          <VerifiedUserOutlined />
          <span className={classes.estateTaskHeaderName}>{params.colDef.headerName}</span>
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <PushToTrustateCell
          {...params}
          estateSearchTaskInstances={estateSearchTaskInstances}
          estateSearchStatusButton={estateSearchStatusButton}
          groupedEstateTaskInstancesAsArray={groupedEstateTaskInstancesAsArray}
          featureSubscriptionTypeData={featureSubscriptionTypeData!}
          setShowPlanPaywallModal={setShowPlanPaywallModal}
          setMinimumFeatureSubscriptionType={setMinimumFeatureSubscriptionType}
          handleAssignClick={handleAssignClick}
          replaceGroupedEstateTaskInstance={replaceGroupedEstateTaskInstance}
        />
      ),
    });

    estateTaskColumns.push({
      field: 'assignedUserId',
      headerName: 'Point Person',
      flex: 2,
      align: 'left',
      headerClassName: 'estateTasksHeader',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box className={classes.estateTaskHeader}>
          <PersonPinOutlined />
          <span className={classes.estateTaskHeaderName}>{params.colDef.headerName}</span>
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <PointPersonCell
          {...params}
          assignedUsersUpdating={assignedUsersUpdating}
          estateTaskInstanceIdUpdatingPointPerson={estateTaskInstanceIdUpdatingPointPerson}
          portalId={portalId}
          showPortalUsersAutocompleteElement={showPortalUsersAutocompleteElement}
          hidePortalUsersAutocompleteElement={hidePortalUsersAutocompleteElement}
          handleAssignedUserChange={handleAssignedUserChange}
          classes={classes}
        />
      ),
    });

    estateTaskColumns.push({
      field: 'quickActions',
      headerName: 'Quick Actions',
      flex: 2.5,
      align: 'center',
      headerClassName: 'estateTasksHeader',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box className={classes.estateTaskHeader}>
          <PersonPinOutlined />
          <span className={classes.estateTaskHeaderName}>{params.colDef.headerName}</span>
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <QuickActionsCell
          {...params}
          estateSearchTaskInstances={estateSearchTaskInstances}
          groupedEstateTaskInstancesAsArray={groupedEstateTaskInstancesAsArray}
          featureSubscriptionTypeData={featureSubscriptionTypeData!}
          handleAddDeadlineClick={handleAddDeadlineClick}
          handleAssignAgainClick={handleAssignAgainClick}
          handleHideTaskClick={handleHideTaskClick}
          handleToggleTaskStatus={handleToggleTaskStatus}
          setCurrentEstateTaskInstance={setCurrentEstateTaskInstance}
          checkboxRefs={checkboxRefs}
        />
      ),
    });

    // estateTaskColumns.push({
    //   field: 'estateTaskInstanceMenu',
    //   headerName: '',
    //   flex: 1,
    //   align: 'center',
    //   headerClassName: 'estateTasksHeader',
    //   renderCell: (params: GridRenderCellParams) => {
    //     const estateTaskInstance = params.row as EstateTaskInstance;

    //     return (
    //       <IconButton
    //         className={classes.estateTaskInstanceMenuIconButton}
    //         id={`estate-task-instance-menu-button-${estateTaskInstance.id}`}
    //         onClick={(e: React.MouseEvent<HTMLElement>) => {
    //           setCurrentEstateTaskInstance(estateTaskInstance);
    //           handleEstateTaskInstanceMenuClick(e);
    //         }}
    //       >
    //         <MoreHoriz />
    //       </IconButton>
    //     );
    //   },
    // });
  }

  if (taskLoading || featureSubscriptionTypeLoading) {
    return <Spinner />;
  }

  return (
    <Box>
      {showPlanPaywallModal && (
        <PlanPaywallModal
          minimumFeatureSubscriptionType={minimumFeatureSubscriptionType}
          showModal={showPlanPaywallModal}
          setShowModal={setShowPlanPaywallModal}
        />
      )}
      {showEstateTaskCustomModal && (
        <EstateTaskCustomModal
          estateId={estate!.id}
          estateType={estate!.type}
          groupedEstateTaskInstances={groupedEstateTaskInstances}
          addGroupedEstateTaskInstance={addGroupedEstateTaskInstance}
          estateTaskGroup={addCustomTaskGroup!}
          showModal={showEstateTaskCustomModal}
          setShowModal={setShowEstateTaskCustomModal} // needed to close modal on outside-click
          onExitClick={() => setShowEstateTaskCustomModal(false)}
        />
      )}
      <Box className={classes.searchBarContainer}>
        <Input
          className={classes.searchBar}
          placeholder="Search my Tasks"
          value={searchTask}
          onChange={(e) => setSearchTask(e.target.value)}
          endAdornment={
            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
              <Search
                className={classes.inputAdornmentStyle}
                onClick={() => {
                  handleSearch();
                }}
              />
              {isSearching && (
                <Close
                  className={classes.inputAdornmentStyle}
                  onClick={() => {
                    handleCancelSearch();
                  }}
                />
              )}
            </InputAdornment>
          }
        />
      </Box>
      {estateSearchTaskInstances.length > 0 && (
        <EstateChecklistStartNewSearchMenu
          estateSearchTaskInstances={estateSearchTaskInstances}
          estateTaskColumns={estateTaskColumns}
          featureSubscriptionTypeLoading={featureSubscriptionTypeLoading}
        />
      )}
      <Box className={classes.taskRowsWrapper}>
        <CreatePaymentModal
          paymentForm={
            <EstateTaskInstancePaymentForm
              estateTaskInstance={estateTaskInstanceCurrentlyEditing}
              setShowModal={setShowDelegatePaymentModal}
              replaceEstateTaskInstance={(
                oldEstateTaskInstance: EstateTaskInstance,
                newEstateTaskInstance: EstateTaskInstance,
              ) => {
                replaceGroupedEstateTaskInstance(
                  oldEstateTaskInstance.group as string,
                  oldEstateTaskInstance.id,
                  newEstateTaskInstance,
                );
              }}
              addEstateTaskInstance={(newEstateTaskInstance: EstateTaskInstance) => {
                addGroupedEstateTaskInstance(
                  newEstateTaskInstance.group as string,
                  newEstateTaskInstance,
                );
              }}
            />
          }
          setShowModal={setShowDelegatePaymentModal}
          showModal={showDelegatePaymentModal}
        />
        {currentEstateTaskInstance && (
          <EstateTaskInstanceMenuMemoized {...currentEstateTaskInstance} />
        )}
        {currentEstateTaskInstance &&
          showEstateTaskInstanceCustomDeadlinePopover &&
          addCustomDeadlineButtonAnchorPosition && (
            <EstateTaskInstanceCustomDeadlinePopover
              estateTaskInstance={currentEstateTaskInstance}
              anchorPosition={addCustomDeadlineButtonAnchorPosition}
              onClose={() => {
                setShowEstateTaskInstanceCustomDeadlinePopover(false);
                handleEstateTaskInstanceMenuClose();
              }}
              replaceEstateTaskInstance={(
                oldEstateTaskInstance: EstateTaskInstance,
                newEstateTaskInstance: EstateTaskInstance,
              ) => {
                replaceGroupedEstateTaskInstance(
                  oldEstateTaskInstance.group as string,
                  oldEstateTaskInstance.id,
                  newEstateTaskInstance,
                );
              }}
            />
          )}
        {currentEstateTaskInstance &&
          currentEstateTaskInstance.estateWorkItemId &&
          showAssignedTaskDrawer && (
            <AssignedTaskDrawer
              estateWorkItemId={currentEstateTaskInstance.estateWorkItemId}
              estate={estate}
              onClose={() => setShowAssignedTaskDrawer(false)}
            />
          )}
        {currentEstateTaskInstance && showHideEstateTaskModal && (
          <HideEstateTaskModal
            estateTaskInstance={currentEstateTaskInstance}
            portalId={portalId}
            showHiddenTasks={showHiddenTasks}
            showModal={showHideEstateTaskModal}
            handleHideTaskCurrentDashboardClick={handleHideTaskCurrentDashboardClick}
            handleHideTaskAllDashboardsClick={handleHideTaskAllDashboardsClick}
            onExitClick={() => setShowHideEstateTaskModal(false)}
          />
        )}

        {Object.keys(groupedEstateTaskInstances).map((group, idx) => {
          const estateTaskInstance = groupedEstateTaskInstances[group].flat() ?? [];

          return (
            <TaskGroupAccordion
              key={group}
              group={group}
              estateTaskInstances={estateTaskInstance}
              groupIndex={idx}
              expanded={!!expandedGroups[group]}
              onToggle={() => {
                setExpandedGroup(group, !expandedGroups[group]);
              }}
              onToggleHidden={() => {
                setShowHiddenTasks(!showHiddenTasks);
                handleShowHiddenTasks(estateTaskInstance[0].group!);
              }}
              onAddCustomTask={handleEstateTaskCustomClick}
              estateTaskColumns={estateTaskColumns}
              featureSubscriptionTypeLoading={featureSubscriptionTypeLoading}
              showHidden={showHiddenTasksMap[group]}
            />
          );
        })}
      </Box>
    </Box>
  );
};
